import React, { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { LayoutDashboard, ScrollText, Timer, Workflow, ListFilter, Users, CalendarDays, UserCog, BrainCircuit, MessageSquare, MessagesSquare, Settings, ChartNoAxesCombined, Inbox, Boxes, Store, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { hasPermission } from '../../utils/permissions';
import ThemePreferenceControl from './ThemePreferenceControl';

const SIDEBAR_STORAGE_KEY = 'jira-sidebar-collapsed';
const navIcons = {
  dashboard: LayoutDashboard, audit_logs: ScrollText, logs: ScrollText, query_trigger_tasks: Timer,
  rules: Workflow, fields: ListFilter, persons: Users, calendars: CalendarDays,
  users: UserCog, ai_settings: BrainCircuit, wecom: MessageSquare,
  wecom_logs: MessagesSquare, jira_settings: Settings,
  ka_dashboard: ChartNoAxesCombined, ka_pending: Inbox, ka_domains: Boxes, ka_settings: Store,
};

const getCurrentPage = (pathname) => {
  if (pathname === '/') return 'dashboard';
  if (pathname.startsWith('/audit-logs')) return 'audit_logs';
  if (pathname.startsWith('/logs')) return 'logs';
  if (pathname.startsWith('/query-trigger-tasks')) return 'query_trigger_tasks';
  if (pathname.startsWith('/rules')) return 'rules';
  if (pathname.startsWith('/fields')) return 'fields';
  if (pathname.startsWith('/persons')) return 'persons';
  if (pathname.startsWith('/calendars')) return 'calendars';
  if (pathname.startsWith('/users')) return 'users';
  if (pathname.startsWith('/ai-settings')) return 'ai_settings';
  if (pathname.startsWith('/wecom/chat-logs')) return 'wecom_logs';
  if (pathname.startsWith('/wecom')) return 'wecom';
  if (pathname.startsWith('/jira-settings')) return 'jira_settings';
  if (pathname.startsWith('/ka-dashboard')) return 'ka_dashboard';
  if (pathname.startsWith('/ka-pending')) return 'ka_pending';
  if (pathname.startsWith('/ka-domains')) return 'ka_domains';
  if (pathname.startsWith('/ka-settings')) return 'ka_settings';
  if (pathname.startsWith('/profile')) return 'profile';
  return 'dashboard';
};

const AppShell = ({ user, onLogout, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = getCurrentPage(location.pathname);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next)); } catch { /* Keep navigation usable when storage is unavailable. */ }
  };
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const accountName = user?.username || user?.email || '当前账号';
  const accountRole = user?.is_admin ? '管理员' : '普通用户';
  const accountInitial = accountName.trim().charAt(0).toUpperCase() || 'U';

  const navItems = [
    ...(hasPermission(user, 'rules:read') ? [{ id: 'dashboard', label: '自动化概览', path: '/', hint: '总览与趋势' }] : []),
    ...(hasPermission(user, 'logs:read') ? [{ id: 'logs', label: '操作记录', path: '/logs', hint: '运行与排障' }] : []),
    ...(hasPermission(user, 'logs:read') ? [{ id: 'query_trigger_tasks', label: '定时触发', path: '/query-trigger-tasks', hint: '查询任务' }] : []),
    ...(hasPermission(user, 'rules:read') ? [{ id: 'rules', label: '规则管理', path: '/rules', hint: '分组与自动化' }] : []),
    ...(hasPermission(user, 'ka_dashboard:read') ? [{ id: 'ka_dashboard', label: 'KA 看板', path: '/ka-dashboard', hint: 'OJML 交付统计' }] : []),
    ...(hasPermission(user, 'ka_settings:read') ? [{ id: 'ka_pending', label: '待归属工单', path: '/ka-pending', hint: '商家归属处理' }] : []),
    ...(hasPermission(user, 'ka_settings:read') ? [{ id: 'ka_domains', label: '产品域管理', path: '/ka-domains', hint: '模块归属与筛选' }] : []),
    ...(hasPermission(user, 'ka_settings:read') ? [{ id: 'ka_settings', label: '商家管理', path: '/ka-settings', hint: '商家规则与同步' }] : []),
    ...(hasPermission(user, 'fields:read') ? [{ id: 'fields', label: '字段管理', path: '/fields', hint: 'Jira 字段映射' }] : []),
    ...(hasPermission(user, 'persons:read') ? [{ id: 'persons', label: '人员管理', path: '/persons', hint: '分配对象' }] : []),
    ...(hasPermission(user, 'calendars:read') ? [{ id: 'calendars', label: '工作日历', path: '/calendars', hint: '节假日与调休' }] : []),
    ...(hasPermission(user, 'jira_settings:read') ? [{ id: 'jira_settings', label: 'Jira 设置', path: '/jira-settings', hint: '系统凭证与 OAuth' }] : []),
    ...(hasPermission(user, 'wecom:read') ? [{ id: 'wecom', label: '企业微信', path: '/wecom', hint: '机器人集成' }] : []),
    ...(hasPermission(user, 'wecom:read') ? [{ id: 'wecom_logs', label: '企业微信日志', path: '/wecom/chat-logs', hint: '聊天与操作' }] : []),
    ...(hasPermission(user, 'users:read') ? [{ id: 'users', label: '用户管理', path: '/users', hint: '账号与权限' }] : []),
    ...(hasPermission(user, 'ai_settings:read') ? [{ id: 'ai_settings', label: 'AI 设置', path: '/ai-settings', hint: '模型提供商' }] : []),
    ...(hasPermission(user, 'audit:read') ? [{ id: 'audit_logs', label: '用户操作审计', path: '/audit-logs', hint: '变更与追溯' }] : []),
  ];

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (err) {
      console.error('登出失败:', err);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    onLogout();
    setMobileNavOpen(false);
    navigate('/login');
  };

  const navigateTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'is-sidebar-collapsed' : ''}`}>
      <aside className={`sidebar ${mobileNavOpen ? 'is-mobile-open' : ''}`}>
        <button aria-label="Jira Automation 首页" title="Jira Automation" className="sidebar-brand" onClick={() => navigateTo('/')}>
          <img className="brand-mark" src={`${process.env.PUBLIC_URL}/brand-logo.svg?v=c`} alt="" aria-hidden="true" />
          <strong>Jira Automation</strong>
        </button>

        <nav className="sidebar-nav" aria-label="主导航">
          {navItems.map(item => {
            const Icon = navIcons[item.id];
            return (
              <Tooltip key={item.id} title={`${item.label} · ${item.hint}`} placement="right" arrow describeChild>
                <button
                  className={`sidebar-link ${currentPage === item.id ? 'active' : ''}`}
                  aria-label={item.label}
                  aria-current={currentPage === item.id ? 'page' : undefined}
                  onClick={() => navigateTo(item.path)}
                >
                  <Icon size={18} aria-hidden="true" />
                  <span className="sidebar-link-label">{item.label}</span>
                </button>
              </Tooltip>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button
            type="button"
            aria-label={`个人资料：${accountName}`}
            title={accountName}
            className={`sidebar-user-card ${currentPage === 'profile' ? 'active' : ''}`}
            onClick={() => navigateTo('/profile')}
          >
            <span className="sidebar-user-avatar" aria-hidden="true">{accountInitial}</span>
            <span className="sidebar-user-details">
              <strong title={accountName}>{accountName}</strong>
              <small>{accountRole}{user?.has_jira_config ? ' · Jira 已配置' : ''}</small>
            </span>
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleLogout}>退出</button>
        </div>
      </aside>
      {mobileNavOpen && <button className="sidebar-backdrop" aria-label="关闭导航" onClick={() => setMobileNavOpen(false)} />}

      <div className="workspace">
        <header className="topbar">
          <div className="topbar-leading">
            <button
              className="mobile-nav-toggle"
              aria-label={mobileNavOpen ? '关闭导航' : '打开导航'}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(open => !open)}
            >
              <span />
              <span />
              <span />
            </button>
            <button
              type="button"
              className="desktop-nav-toggle"
              aria-label={sidebarCollapsed ? '展开侧栏' : '收起侧栏'}
              title={sidebarCollapsed ? '展开侧栏' : '收起侧栏'}
              aria-expanded={!sidebarCollapsed}
              onClick={toggleSidebar}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={20} aria-hidden="true" /> : <PanelLeftClose size={20} aria-hidden="true" />}
            </button>
            <span className="topbar-kicker">管理后台</span>
          </div>
          <div className="topbar-actions">
            <ThemePreferenceControl />
          </div>
        </header>
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
